import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Activity, RefreshCw, CheckCircle2, AlertTriangle, XCircle, Clock,
  Zap, Image, Film, Music, Mic, User, FileText, Wifi, WifiOff,
  Shield, Server, CreditCard, Play, ChevronRight, ExternalLink
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  healthy: { color: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', icon: CheckCircle2, label: 'Healthy' },
  degraded: { color: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', icon: AlertTriangle, label: 'Degraded' },
  error: { color: 'bg-red-500', text: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', icon: XCircle, label: 'Error' },
  critical: { color: 'bg-red-600', text: 'text-red-800', bg: 'bg-red-50', border: 'border-red-300', icon: XCircle, label: 'Critical' },
  timeout: { color: 'bg-gray-500', text: 'text-gray-700', bg: 'bg-gray-50', border: 'border-gray-200', icon: Clock, label: 'Timeout' },
  unknown: { color: 'bg-gray-400', text: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200', icon: Clock, label: 'Unknown' },
  available: { color: 'bg-blue-500', text: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200', icon: CheckCircle2, label: 'Available' },
  ok: { color: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', icon: CheckCircle2, label: 'OK' },
};

const CATEGORY_ICONS = {
  text_chat: FileText,
  image: Image,
  video: Film,
  music: Music,
  tts: Mic,
  avatar: User,
};

function StatusDot({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.unknown;
  return <div className={cn("w-2.5 h-2.5 rounded-full", cfg.color)} title={cfg.label} />;
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.unknown;
  return <Badge className={cn("text-[10px] gap-1 font-medium", cfg.bg, cfg.text, cfg.border)}><cfg.icon className="h-3 w-3" />{cfg.label}</Badge>;
}

export default function KaharianHealthPage() {
  const [providerStatus, setProviderStatus] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [creditData, setCreditData] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [testResult, setTestResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [testing, setTesting] = useState(false);

  const fetchAll = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setRefreshing(true);
    try {
      const [ps, hd, cr, logs] = await Promise.allSettled([
        axios.get(`${API}/api/kaharian-health/provider-status`),
        axios.get(`${API}/api/kaharian-health/health`),
        axios.get(`${API}/api/kaharian-health/credits`),
        axios.get(`${API}/api/kaharian-health/recent-issues`),
      ]);
      if (ps.status === 'fulfilled') setProviderStatus(ps.value.data);
      if (hd.status === 'fulfilled') setHealthData(hd.value.data);
      if (cr.status === 'fulfilled') setCreditData(cr.value.data);
      if (logs.status === 'fulfilled') setRecentLogs(logs.value.data.logs || []);
      if (!silent) toast.success('Health refreshed');
    } catch (err) {
      if (!silent) toast.error('Refresh failed — check console');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchAll({ silent: true }); }, [fetchAll]);

  const runTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const res = await axios.post(`${API}/api/kaharian-health/test-connection`, {});
      setTestResult(res.data);
      toast.success(`Connection test complete: ${res.data.overall}`);
      fetchAll({ silent: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test failed');
    } finally { setTesting(false); }
  };

  const overall = providerStatus?.overall || 'unknown';
  const overallCfg = STATUS_CONFIG[overall] || STATUS_CONFIG.unknown;

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
    </div>
  );

  return (
    <div className="space-y-5 animate-fadeIn" data-testid="kaharian-health-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">API Integration Health</h1>
            <StatusBadge status={overall} />
          </div>
          <p className="text-gray-medium text-sm mt-1">Monitor AI gateway connectivity, provider health, and credit usage</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => fetchAll()} disabled={refreshing} data-testid="refresh-health">
            <RefreshCw className={cn("h-3.5 w-3.5 mr-1", refreshing && "animate-spin")} />Refresh
          </Button>
          <Button size="sm" onClick={runTest} disabled={testing} data-testid="run-test-btn">
            {testing ? <RefreshCw className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Play className="h-3.5 w-3.5 mr-1" />}
            Run Connection Test
          </Button>
        </div>
      </div>

      {/* Overall Status Bar */}
      <Card className={cn("border-2", overallCfg.border, overallCfg.bg)}>
        <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", overallCfg.color)}>
              <overallCfg.icon className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className={cn("text-sm font-bold", overallCfg.text)}>Gateway Status: {overallCfg.label}</p>
              <p className="text-xs text-gray-500">
                {providerStatus?.healthy_count || 0}/{providerStatus?.tested_count || providerStatus?.total || 0} tested providers healthy
                {healthData?.latency_ms ? ` · ${healthData.latency_ms}ms latency` : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4 text-xs text-gray-500 flex-wrap">
            {(providerStatus?.gateway_version || healthData?.gateway_version) && (
              <span className="font-medium text-gray-700">
                Gateway v{providerStatus?.gateway_version || healthData?.gateway_version}
                {(providerStatus?.gateway_codename || healthData?.gateway_codename) && (
                  <span className="text-gray-500 ml-1">· {providerStatus?.gateway_codename || healthData?.gateway_codename}</span>
                )}
                {providerStatus?.sdk_version && <span className="text-gray-400 ml-1">· SDK {providerStatus.sdk_version}</span>}
              </span>
            )}
            {providerStatus?.gateway_summary?.total > 0 && (
              <span>Upstream: {providerStatus.gateway_summary.healthy || 0}/{providerStatus.gateway_summary.tested || 0} tested healthy ({providerStatus.gateway_summary.total} total)</span>
            )}
            {providerStatus?.last_test && (
              <span>Last test: {new Date(providerStatus.last_test).toLocaleString()}</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Ownership banner — distinguishes Hub-side outage from Konnect issues */}
      {providerStatus && providerStatus.owner === 'hub' && providerStatus.overall !== 'healthy' && (
        <Card className="bg-blue-50 border-blue-200" data-testid="ownership-banner">
          <CardContent className="p-3 flex items-start gap-2.5">
            <Server className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-blue-900 leading-relaxed">
              <span className="font-semibold">This is a Kaharian Hub-side outage, not a Konnect issue.</span>
              {' '}Konnect's integration is fully compliant (v10 contract, Idempotency-Keys, async polling all verified).
              Affected categories will auto-recover when the Hub's upstream providers come back online — no action required from you.
              {' '}<a href="https://kaharian.com/status" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-700 inline-flex items-center gap-0.5">Hub status page <ExternalLink className="h-3 w-3" /></a>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Provider Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {providerStatus?.providers && Object.entries(providerStatus.providers).map(([key, prov]) => {
          const Icon = CATEGORY_ICONS[key] || Zap;
          const st = prov.status || 'unknown';
          const cfg = STATUS_CONFIG[st] || STATUS_CONFIG.unknown;
          return (
            <Card key={key} className={cn("border bg-white hover:shadow-sm transition-shadow")} data-testid={`provider-${key}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2.5">
                    <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", cfg.bg)}>
                      <Icon className={cn("h-4 w-4", cfg.text)} />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-text-primary">{prov.name}</p>
                      <p className="text-[10px] text-gray-medium uppercase tracking-wider">{prov.category}</p>
                    </div>
                  </div>
                  <StatusDot status={st} />
                </div>
                {prov.note && <p className="text-[11px] text-gray-500 mt-1 leading-relaxed">{prov.note}</p>}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Credit Status + Connection Test Results */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Credits */}
        <Card className="bg-white border-gray-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-bold flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-amber-500" />Credit Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            {creditData?.balance ? (
              <div className="space-y-3">
                {Object.entries(creditData.balance).filter(([k]) => !['timestamp', 'error'].includes(k)).map(([key, val]) => (
                  <div key={key} className="flex items-center justify-between">
                    <span className="text-xs text-gray-medium capitalize">{key.replace(/_/g, ' ')}</span>
                    <span className="text-sm font-bold text-text-primary">{typeof val === 'number' ? val.toLocaleString() : String(val)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-400">Credit data unavailable — run a connection test</p>
            )}
          </CardContent>
        </Card>

        {/* Test Results */}
        <Card className="bg-white border-gray-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-bold flex items-center gap-2">
              <Activity className="h-4 w-4 text-blue-500" />Connection Test Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            {testResult ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 mb-3">
                  <StatusBadge status={testResult.overall} />
                  <span className="text-[10px] text-gray-400">{new Date(testResult.timestamp).toLocaleString()}</span>
                </div>
                {Object.entries(testResult.results).map(([key, val]) => (
                  <div key={key} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                    <div className="flex items-center gap-2">
                      <StatusDot status={val?.status || 'unknown'} />
                      <span className="text-xs font-medium capitalize">{key.replace(/_/g, ' ')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {val?.latency_ms && <span className="text-[10px] text-gray-400">{val.latency_ms}ms</span>}
                      {val?.provider && <Badge className="text-[9px] bg-gray-100 text-gray-500">{val.provider}</Badge>}
                      {val?.error && <span className="text-[10px] text-red-400 max-w-[150px] truncate">{val.error}</span>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <Activity className="h-10 w-10 mx-auto mb-2 text-gray-200" />
                <p className="text-xs text-gray-400">Click "Run Connection Test" to check all providers</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Test History */}
      <Card className="bg-white border-gray-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-bold flex items-center gap-2">
            <Clock className="h-4 w-4 text-gray-500" />Recent Health Checks
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentLogs.length === 0 ? (
            <p className="text-xs text-gray-400 py-4 text-center">No test history yet</p>
          ) : (
            <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
              {recentLogs.map((log, i) => (
                <div key={i} className="flex items-center justify-between py-2 px-2 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-2.5">
                    <StatusDot status={log.overall_status} />
                    <div>
                      <p className="text-xs font-medium capitalize">{log.type?.replace(/_/g, ' ')}</p>
                      <p className="text-[10px] text-gray-400">by {log.tested_by_name || 'System'}</p>
                    </div>
                  </div>
                  <span className="text-[10px] text-gray-400">
                    {log.created_at ? new Date(log.created_at).toLocaleString() : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ── Compact Health Badge for Dashboard ───────────────────────────────────── */
export function KaharianHealthBadge() {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/kaharian-health/provider-status`)
      .then(r => setData(r.data))
      .catch(() => {});
  }, []);

  if (!data) return null;
  const overall = data.overall || 'unknown';
  const cfg = STATUS_CONFIG[overall] || STATUS_CONFIG.unknown;
  const OverallIcon = overall === 'healthy' ? Wifi : WifiOff;

  return (
    <Card className={cn("border bg-white hover:shadow-sm transition-shadow cursor-pointer")} data-testid="kaharian-health-badge">
      <CardContent className="p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", cfg.bg)}>
              <OverallIcon className={cn("h-4 w-4", cfg.text)} />
            </div>
            <div>
              <p className="text-xs font-bold text-text-primary">AI Gateway</p>
              <p className="text-[10px] text-gray-medium">{data.healthy_count}/{data.tested_count || data.total} tested up</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <StatusBadge status={overall} />
            <ChevronRight className="h-3.5 w-3.5 text-gray-300" />
          </div>
        </div>
        {/* Mini provider dots */}
        {data.providers && (
          <div className="flex gap-1.5 mt-2">
            {Object.entries(data.providers).map(([key, prov]) => (
              <div key={key} className="flex items-center gap-0.5" title={`${prov.name}: ${prov.status}`}>
                <StatusDot status={prov.status || 'unknown'} />
                <span className="text-[9px] text-gray-400">{key.replace('_', ' ').slice(0, 5)}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
